import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  ConnectorListResponseSchema,
  ConnectorSummarySchema,
  ConnectorTestResponseSchema,
  type ConnectorProvider,
  type ConnectorSummary,
  type ConnectorTestResponse,
  type ImplementedProvider,
} from "@hyphesync/shared";
import { ApiError, describeError, useApi } from "../lib/api";
import { resume as resumeAction, runNow as runNowAction } from "../lib/connectorActions";
import { btn, btnPrimary, fmtDateTime as fmtDate, inputClass } from "../lib/ui";
import { useVisiblePoll } from "../lib/usePoll";
import {
  CONFIRM,
  cardErrorSawUpdate,
  cardLocks,
  createLatestGate,
  errorStillApplies,
  newCardError,
  restartsEditForm,
  type CardError,
  type Panel,
  PROVIDER_FORMS,
  PROVIDER_LABEL,
  cadenceLabel,
  cadenceOptions,
  changedAccountFields,
  connectorPatch,
  defaultFormValues,
  isImplemented,
  parseForm,
  toFormValues,
  type Field,
  type FormValues,
} from "../lib/connectors";


/** Settings → Connectors (admin only; the API enforces it, the nav hides it). */
export function Connectors() {
  const api = useApi();
  const [list, setList] = useState<ConnectorSummary[] | null>(null);
  const [providers, setProviders] = useState<ConnectorProvider[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Every load and every applied mutation answer takes a number; a load's answer is applied only
  // if nothing newer has been (a poll that read before an admin's change must not undo it on
  // screen). Returns the fresh list, or null when it was superseded or failed.
  const [gate] = useState(createLatestGate); // lazy: built once, not on every render
  const load = useCallback(async (): Promise<ConnectorSummary[] | null> => {
    const mine = gate.begin();
    try {
      const res = ConnectorListResponseSchema.parse(await api("GET", "/api/connectors"));
      if (!gate.isCurrent(mine)) return null;
      setList(res.connectors);
      setProviders(res.availableProviders);
      setLoadError(null);
      return res.connectors;
    } catch (e) {
      if (gate.isCurrent(mine)) setLoadError(describeError(e));
      return null;
    }
  }, [api, gate]);
  const poll = useCallback(async () => { await load(); }, [load]);

  // Refresh every minute while visible, and on coming back to the tab, so a connector the
  // framework paused (OD-33) or a run's failure shows here without a reload. load() catches
  // everything itself.
  useVisiblePoll(poll);

  /** Put one connector's fresh state from a mutation answer into the list. */
  const replace = (c: ConnectorSummary) => {
    gate.invalidate(); // any load already in flight read the state before this change
    setList((l) => (l ? l.map((x) => (x.id === c.id ? c : x)) : l));
  };

  const addable = providers.filter(isImplemented);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Connectors</h1>
        <p className="mt-1 text-muted">
          The provider accounts HypheSync reads from. Credentials are write-only: once saved they are never shown again.
          Follow runs on <Link to="/sync-health" className="font-medium text-brand underline">Sync health</Link>.
        </p>
      </div>

      {loadError && (
        <p role="alert" className="rounded-lg border border-line bg-card p-4 text-bad">
          {loadError}{" "}
          <button type="button" onClick={() => void load()} className="touch-target font-medium text-brand underline">Retry</button>
        </p>
      )}

      {list !== null && (addable.length > 0
        ? <AddConnector providers={addable} onAdded={(c) => { gate.invalidate(); setList((l) => [...(l ?? []), c]); }} />
        : <p className="text-muted">No connector types can be added in this environment yet.</p>)}

      <section aria-labelledby="configured" className="flex flex-col gap-3">
        <h2 id="configured" className="text-lg font-semibold">Configured</h2>
        {list === null && !loadError && <p className="text-muted">Loading…</p>}
        {list?.length === 0 && <p className="text-muted">None yet.</p>}
        {list?.map((c) => <ConnectorCard key={c.id} connector={c} onChanged={replace} onReload={load} />)}
      </section>
    </div>
  );
}

function FieldInput({ field, value, onChange, idPrefix }: { field: Field; value: string; onChange: (v: string) => void; idPrefix: string }) {
  const id = `${idPrefix}-${field.key}`;
  const helpId = field.help ? `${id}-help` : undefined;
  let control;
  if (field.kind === "select") {
    control = (
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-describedby={helpId} className={inputClass}>
        {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else {
    control = (
      <input
        id={id}
        type={field.kind === "secret" ? "password" : field.kind === "number" ? "number" : "text"}
        inputMode={field.kind === "number" ? "numeric" : undefined}
        min={field.kind === "number" ? field.min : undefined}
        max={field.kind === "number" ? field.max : undefined}
        maxLength={field.kind === "text" ? field.maxLength : undefined}
        // A credential is never filled in by the browser or a password manager's guess, and
        // never pre-filled by us (D8: write-only).
        autoComplete={field.kind === "secret" ? "new-password" : "off"}
        required={field.kind === "secret"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={helpId}
        className={`${inputClass} min-w-0`}
      />
    );
  }
  return (
    <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1 text-sm font-medium">
      <label htmlFor={id}>{field.label}</label>
      {control}
      {field.help && <span id={helpId} className="font-normal text-muted">{field.help}</span>}
    </div>
  );
}

function CadenceSelect({ id, value, onChange }: { id: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex flex-col gap-1 text-sm font-medium">
      <label htmlFor={id}>Runs</label>
      <select id={id} value={value} onChange={(e) => onChange(Number(e.target.value))} className={inputClass}>
        {cadenceOptions(value).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function AddConnector({ providers, onAdded }: { providers: ImplementedProvider[]; onAdded: (c: ConnectorSummary) => void }) {
  const api = useApi();
  const [provider, setProvider] = useState<ImplementedProvider>(providers[0]!);
  const [name, setName] = useState("");
  const [values, setValues] = useState<FormValues>(() => defaultFormValues(providers[0]!));
  const [cadence, setCadence] = useState(300);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pick(p: ImplementedProvider) {
    setProvider(p);
    setValues(defaultFormValues(p));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const config = parseForm(provider, "config", values);
    if (!config.ok) return setError(config.message);
    setBusy(true);
    try {
      const res = await api<{ connector: unknown }>("POST", "/api/connectors", { provider, name, config: config.value, cadenceSecs: cadence });
      onAdded(ConnectorSummarySchema.parse(res.connector));
      setName("");
      setValues(defaultFormValues(provider));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="add" className="rounded-xl border border-line bg-card p-5">
      <h2 id="add" className="text-lg font-semibold">Add a connector</h2>
      <p className="mt-1 text-sm text-muted">It starts switched off. Add its credentials, Test them, then turn it on.</p>
      <form onSubmit={(e) => void submit(e)} className="mt-4 flex flex-col gap-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex flex-col gap-1 text-sm font-medium">
            <label htmlFor="add-provider">Provider</label>
            <select id="add-provider" value={provider} onChange={(e) => pick(e.target.value as ImplementedProvider)} className={inputClass}>
              {providers.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
            </select>
          </div>
          <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1 text-sm font-medium">
            <label htmlFor="add-name">Name</label>
            <input id="add-name" required maxLength={80} autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} min-w-0`} />
          </div>
          <CadenceSelect id="add-cadence" value={cadence} onChange={setCadence} />
        </div>
        <div className="flex flex-wrap items-start gap-3">
          {PROVIDER_FORMS[provider].config.map((f) => (
            <FieldInput key={f.key} idPrefix="add" field={f} value={values[f.key] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy} className={btnPrimary}>{busy ? "Adding…" : "Add connector"}</button>
          {error && <p role="alert" className="text-bad">{error}</p>}
        </div>
      </form>
    </section>
  );
}


function ConnectorCard({ connector: c, onChanged, onReload }: {
  connector: ConnectorSummary;
  onChanged: (c: ConnectorSummary) => void;
  /** Reload the list; resolves to the fresh list, or null if superseded or failed. */
  onReload: () => Promise<ConnectorSummary[] | null>;
}) {
  const api = useApi();
  const [panel, setPanel] = useState<Panel>(null);
  // The edit form's identity. It changes ONLY when a save is refused as stale, so the form then
  // restarts from the current values; a background refresh (another admin's edit arriving) never
  // wipes what is being typed, and the save's own version check (409) catches that edit.
  const [editEpoch, setEditEpoch] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<CardError | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [testResult, setTestResult] = useState<ConnectorTestResponse | null>(null);
  const implemented = isImplemented(c.provider);
  const paused = c.status === "needs_attention";

  async function act(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      await refused(e);
    } finally {
      setBusy(null);
    }
  }

  /** Show a refusal and reload (it may mean this card is stale). The error is pinned to the first
   *  connector state that ANY load shows afterwards (the effect below), so a reload superseded by
   *  a poll still pins it to the true state, and a later refresh that changes it clears it. */
  async function refused(e: unknown) {
    setError(newCardError(describeError(e), e instanceof ApiError ? e.code : null));
    await onReload();
  }
  // Each applied load or mutation answer gives this card a new `c` object.
  useEffect(() => {
    setError((cur) => (cur ? cardErrorSawUpdate(cur, c) : cur));
  }, [c]);
  const shownError = error && errorStillApplies(error, c) ? error.text : null;

  const patch = (body: Record<string, unknown>) =>
    api<{ connector: unknown }>("PATCH", `/api/connectors/${c.id}`, { ...body, ifUpdatedAt: c.updatedAt })
      .then((r) => onChanged(ConnectorSummarySchema.parse(r.connector)));

  const toggle = () => {
    if (c.enabled && !window.confirm(CONFIRM.turnOff(c.name))) return;
    void act("toggle", () => patch({ enabled: !c.enabled }));
  };
  const resume = () =>
    void act("resume", async () => {
      const res = await resumeAction(api, c);
      if (res) onChanged(ConnectorSummarySchema.parse(res.connector));
    });
  const runNow = () =>
    void act("run", async () => {
      if (await runNowAction(api, c)) setNotice({ ok: true, text: "Queued. Follow it on Sync health." });
    });
  const test = () =>
    void act("test", async () => {
      setTestResult(null);
      setTestResult(ConnectorTestResponseSchema.parse(await api("POST", `/api/connectors/${c.id}/test`)));
    });

  const cardBusy = busy !== null;
  // While a panel holds typed input, what would throw it away is locked (lib/connectors.ts).
  const locks = cardLocks(panel);
  const editing = panel === "edit";
  return (
    <article aria-labelledby={`c-${c.id}`} className="rounded-xl border border-line bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id={`c-${c.id}`} className="text-base font-semibold">{c.name}</h3>
          <p className="text-sm text-muted">{PROVIDER_LABEL[c.provider]} · {cadenceLabel(c.cadenceSecs)}</p>
        </div>
        <p className="text-sm font-medium">
          {paused
            ? <span className="text-bad">Paused: credentials refused</span>
            : c.enabled ? <span className="text-ok">On</span> : <span className="text-muted">Off</span>}
        </p>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex gap-2"><dt className="text-muted">Credentials</dt><dd>{c.hasCredentials ? `Set · last rotated ${fmtDate(c.credLastRotatedAt)}` : <span className="text-bad">Not set</span>}</dd></div>
        <div className="flex gap-2"><dt className="text-muted">Last success</dt><dd>{fmtDate(c.lastSuccessAt)}</dd></div>
        {c.lastError && <div className="flex gap-2 sm:col-span-2"><dt className="text-muted">Last error</dt><dd className="min-w-0 break-words text-bad">{c.lastError}</dd></div>}
      </dl>

      {!implemented && <p className="mt-3 text-sm text-muted">This provider can't be edited in this environment.</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        {paused && <button type="button" disabled={cardBusy || locks.resume} onClick={resume} className={btnPrimary}>{busy === "resume" ? "Resuming…" : "Resume"}</button>}
        <button
          type="button"
          disabled={cardBusy || locks.toggle || (!c.enabled && !c.hasCredentials)}
          onClick={toggle}
          className={btn}
          aria-describedby={!c.enabled && !c.hasCredentials ? `c-${c.id}-needs-creds` : undefined}
        >
          {c.enabled ? "Turn off" : "Turn on"}
        </button>
        <button type="button" disabled={cardBusy || !c.hasCredentials} onClick={test} className={btn}>{busy === "test" ? "Testing…" : "Test"}</button>
        <button type="button" disabled={cardBusy || !c.enabled || paused} onClick={runNow} className={btn}>{busy === "run" ? "Queuing…" : "Run now"}</button>
        {implemented && (
          <>
            <button type="button" disabled={locks.editButton} aria-expanded={panel === "edit"} onClick={() => setPanel(panel === "edit" ? null : "edit")} className={btn}>{editing ? "Close edit" : "Edit"}</button>
            <button type="button" disabled={locks.credentialsButton} aria-expanded={panel === "credentials"} onClick={() => setPanel(panel === "credentials" ? null : "credentials")} className={btn}>
              {panel === "credentials" ? "Close credentials" : c.hasCredentials ? "Replace credentials" : "Add credentials"}
            </button>
          </>
        )}
      </div>
      {!c.enabled && !c.hasCredentials && <p id={`c-${c.id}-needs-creds`} className="mt-2 text-sm text-muted">Add credentials before turning it on.</p>}
      {editing && <p className="mt-2 text-sm text-muted">Save or close the edit to change credentials, turn it on or off{paused ? ", or resume it" : ""}.</p>}
      {panel === "credentials" && <p className="mt-2 text-sm text-muted">Save or close the credentials to edit the settings.</p>}

      {testResult && (
        <p role="status" className={`mt-3 text-sm ${testResult.ok ? "text-ok" : "text-bad"}`}>
          {testResult.ok ? "Test passed: " : "Test failed: "}{testResult.message}
        </p>
      )}
      {notice && <p role="status" className="mt-3 text-sm text-ok">{notice.text} <Link to="/sync-health" className="font-medium text-brand underline">Sync health</Link></p>}
      {shownError && <p role="alert" className="mt-3 text-sm text-bad">{shownError}</p>}

      {implemented && panel === "edit" && (
        // After a refused save the form restarts (new epoch) from the CURRENT values, instead of
        // re-sending the old ones under the new version (that reverted the other admin's edit).
        <EditPanel
          key={editEpoch}
          connector={c}
          provider={c.provider as ImplementedProvider}
          onSaved={(n) => { onChanged(n); setPanel(null); }}
          onStale={(e) => { void refused(e).then(() => setEditEpoch((n) => n + 1)); }}
        />
      )}
      {implemented && panel === "credentials" && (
        <CredentialsPanel connector={c} provider={c.provider as ImplementedProvider} onSaved={(n) => { onChanged(n); setPanel(null); }} />
      )}
    </article>
  );
}

function EditPanel({ connector: c, provider, onSaved, onStale }: {
  connector: ConnectorSummary;
  provider: ImplementedProvider;
  onSaved: (c: ConnectorSummary) => void;
  /** The save was refused as STALE (restartsEditForm): the card shows why, reloads and
   *  restarts this form from the current values. Any other refusal stays in the form. */
  onStale: (e: unknown) => void;
}) {
  const api = useApi();
  const fields = PROVIDER_FORMS[provider].config;
  // The form's baseline and its version check are ONE snapshot, taken when it opened: a save
  // can only ever be "these changes against the version I was shown".
  const [base] = useState(() => ({ name: c.name, config: c.config, cadenceSecs: c.cadenceSecs, updatedAt: c.updatedAt }));
  const [name, setName] = useState(c.name);
  const [values, setValues] = useState<FormValues>(() => toFormValues(fields, c.config));
  const [cadence, setCadence] = useState(c.cadenceSecs);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const config = parseForm(provider, "config", values, "edit");
    if (!config.ok) return setError(config.message);
    const body = connectorPatch(base, { name, config: config.value, cadenceSecs: cadence });
    if (Object.keys(body).length === 0) return setError("Nothing has changed.");
    const moved = body.config ? changedAccountFields(provider, base.config, config.value) : [];
    if (moved.length > 0 && !window.confirm(CONFIRM.accountChange(base.name, moved))) return;
    setBusy(true);
    let saved: ConnectorSummary;
    try {
      const res = await api<{ connector: unknown }>("PATCH", `/api/connectors/${c.id}`, { ...body, ifUpdatedAt: base.updatedAt });
      saved = ConnectorSummarySchema.parse(res.connector);
    } catch (err) {
      setBusy(false);
      if (restartsEditForm(err instanceof ApiError ? err.code : null)) onStale(err);
      else setError(describeError(err)); // keep what was typed, so it can be corrected
      return;
    }
    setBusy(false);
    onSaved(saved);
  }

  return (
    <form onSubmit={(e) => void submit(e)} aria-label={`Edit ${c.name}`} className="mt-4 flex flex-col gap-4 rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 flex-1 basis-48 flex-col gap-1 text-sm font-medium">
          <label htmlFor={`edit-${c.id}-name`}>Name</label>
          <input id={`edit-${c.id}-name`} required maxLength={80} autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} className={`${inputClass} min-w-0`} />
        </div>
        <CadenceSelect id={`edit-${c.id}-cadence`} value={cadence} onChange={setCadence} />
      </div>
      <div className="flex flex-wrap items-start gap-3">
        {fields.map((f) => (
          <FieldInput key={f.key} idPrefix={`edit-${c.id}`} field={f} value={values[f.key] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className={btnPrimary}>{busy ? "Saving…" : "Save"}</button>
        {error && <p role="alert" className="text-sm text-bad">{error}</p>}
      </div>
    </form>
  );
}

function CredentialsPanel({ connector: c, provider, onSaved }: {
  connector: ConnectorSummary;
  provider: ImplementedProvider;
  onSaved: (c: ConnectorSummary) => void;
}) {
  const api = useApi();
  const fields = PROVIDER_FORMS[provider].credentials;
  // Always starts empty: the API never returns credentials (D8), and nothing here keeps them
  // after a save.
  const empty = () => Object.fromEntries(fields.map((f) => [f.key, ""]));
  const [values, setValues] = useState<FormValues>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const creds = parseForm(provider, "credentials", values);
    if (!creds.ok) return setError(creds.message);
    if (c.hasCredentials && !window.confirm(CONFIRM.replaceCredentials(c.name))) return;
    setBusy(true);
    try {
      const res = await api<{ connector: unknown }>("PUT", `/api/connectors/${c.id}/credentials`, { credentials: creds.value });
      setValues(empty());
      onSaved(ConnectorSummarySchema.parse(res.connector));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} aria-label={`Credentials for ${c.name}`} className="mt-4 flex flex-col gap-4 rounded-lg border border-line bg-surface p-4">
      <p className="text-sm text-muted">
        {c.hasCredentials
          ? `Stored credentials are never shown. Last rotated ${fmtDate(c.credLastRotatedAt)}. Saving replaces them and clears a pause.`
          : "Stored encrypted. Once saved they are never shown again, only when they were last rotated."}
      </p>
      <div className="flex flex-wrap items-start gap-3">
        {fields.map((f) => (
          <FieldInput key={f.key} idPrefix={`cred-${c.id}`} field={f} value={values[f.key] ?? ""} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className={btnPrimary}>{busy ? "Saving…" : "Save credentials"}</button>
        {error && <p role="alert" className="text-sm text-bad">{error}</p>}
      </div>
    </form>
  );
}
